/**
 * Date formatting utilities for consistent date display across the HR module.
 * All dates use Indian locale (en-IN) for consistency.
 */

/**
 * Format date as "15 Jan 2026" (standard display format)
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; // Return as-is if invalid
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format date as "15 Jan" (compact format for tables)
 */
export function formatDateCompact(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format date as "15 January 2026" (full format for headers/titles)
 */
export function formatDateFull(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format date with time: "15 Jan 2026, 10:30 AM"
 */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format relative time: "2 hours ago", "Yesterday", "3 days ago"
 */
/**
 * Format time string as 12-hour format: "10:30 AM"
 * Accepts HH:MM (24h) or ISO datetime strings.
 */
export function formatTime12(timeStr: string | null | undefined): string {
  if (!timeStr) return "—";
  try {
    // If it looks like a pure time string "HH:MM" or "HH:MM:SS"
    const timeOnly = /^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr);
    if (timeOnly) {
      const [h, m] = timeStr.split(":").map(Number);
      if (h >= 0 && h < 24 && m >= 0 && m < 60) {
        const period = h >= 12 ? "PM" : "AM";
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
      }
    }
    // Try as a full datetime string
    const date = new Date(timeStr);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    }
    return timeStr;
  } catch {
    return timeStr;
  }
}

/**
 * Format time string as 24-hour format: "10:30" or "22:30"
 * Accepts HH:MM or ISO datetime strings.
 */
export function formatTime24(timeStr: string | null | undefined): string {
  if (!timeStr) return "—";
  try {
    const timeOnly = /^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr);
    if (timeOnly) {
      const [h, m] = timeStr.split(":").map(Number);
      if (h >= 0 && h < 24 && m >= 0 && m < 60) {
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      }
    }
    const date = new Date(timeStr);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    return timeStr;
  } catch {
    return timeStr;
  }
}

/**
 * Format relative time: "2 hours ago", "Yesterday", "3 days ago"
 */
export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return formatDate(dateStr);
  } catch {
    return dateStr;
  }
}
